import React from 'react';
import { Button } from '@Comp';
import { FaCamera, FaUpload, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './faceVerification.scss';

class FaceVerification extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            captureMethod: 'camera', // 'camera' or 'upload'
            imagePreview: null,
            imageData: null,
            stream: null,
            isCapturing: false,
            verificationStatus: null, // null, 'verifying', 'success', 'failed'
            confidenceScore: null,
            errorMessage: null,
            apiError: null, // API-specific errors (non-blocking)
        };
        this.videoRef = React.createRef();
        this.canvasRef = React.createRef();
        this.fileInputRef = React.createRef();
    }

    componentWillUnmount() {
        this.stopCamera();
    }

    handleMethodChange = (method) => {
        if (this.state.captureMethod !== method) {
            this.setState({
                captureMethod: method,
                imagePreview: null,
                imageData: null,
                verificationStatus: null,
                confidenceScore: null,
                errorMessage: null,
                apiError: null,
            });
            if (method === 'upload') {
                this.stopCamera();
            }
        }
    };

    startCamera = async () => {
        try {
            this.setState({ isCapturing: true, errorMessage: null });
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                }
            });

            if (this.videoRef.current) {
                this.videoRef.current.srcObject = stream;
                this.setState({ stream });
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            this.setState({
                errorMessage: 'Unable to access camera. Please check permissions or use file upload.',
                isCapturing: false
            });
        }
    };

    stopCamera = () => {
        const { stream } = this.state;
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            this.setState({ stream: null, isCapturing: false });
        }
    };

    capturePhoto = () => {
        const video = this.videoRef.current;
        const canvas = this.canvasRef.current;

        if (video && canvas) {
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64data = reader.result;
                    this.setState({
                        imagePreview: base64data,
                        imageData: base64data,
                    });
                    this.stopCamera();
                };
                reader.readAsDataURL(blob);
            }, 'image/jpeg', 0.95);
        }
    };

    handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                this.setState({
                    errorMessage: 'Please upload a valid image file (JPEG, PNG, etc.)'
                });
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.setState({
                    errorMessage: 'Image size should be less than 5MB'
                });
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64data = reader.result;
                this.setState({
                    imagePreview: base64data,
                    imageData: base64data,
                    errorMessage: null,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    handleRetake = () => {
        this.setState({
            imagePreview: null,
            imageData: null,
            verificationStatus: null,
            confidenceScore: null,
            errorMessage: null,
            apiError: null,
        });
        if (this.state.captureMethod === 'camera') {
            this.startCamera();
        }
    };

    handleVerify = async () => {
        const { imageData } = this.state;
        const { idNumber, onVerificationComplete, onSuccess } = this.props;

        if (!imageData) {
            this.setState({ errorMessage: 'Please capture or upload an image first' });
            return;
        }

        this.setState({
            verificationStatus: 'verifying',
            errorMessage: null,
            apiError: null,
        });

        try {
            // Call verification API
            const response = await onVerificationComplete(idNumber, imageData);

            if (response.match) {
                this.setState({
                    verificationStatus: 'success',
                    confidenceScore: response.confidenceScore,
                    apiError: null,
                });

                // Notify parent component of success
                if (onSuccess) {
                    onSuccess(response);
                }
            } else {
                this.setState({
                    verificationStatus: 'failed',
                    confidenceScore: response.confidenceScore,
                    errorMessage: response.message || 'Face verification failed. Please try again.',
                });
            }
        } catch (error) {
            console.error('Verification error:', error);

            this.setState({
                verificationStatus: 'failed',
                apiError: 'Something went wrong. You can skip this step.',
            });
            // Don't throw - allow user to retry or skip
        }
    };

    render() {
        const {
            captureMethod,
            imagePreview,
            isCapturing,
            verificationStatus,
            confidenceScore,
            errorMessage,
            apiError,
        } = this.state;

        return (
            <div className="face-verification-container">
                <h4 className="verification-heading">Face Verification</h4>
                <p className="verification-description">
                    Please take a selfie or upload a photo to verify your identity
                </p>

                {/* Method Selection */}
                {!imagePreview && !isCapturing && (
                    <div className="method-selection">
                        <button
                            className={`method-button ${captureMethod === 'camera' ? 'active' : ''}`}
                            onClick={() => this.handleMethodChange('camera')}
                        >
                            <FaCamera className="method-icon" />
                            <span>Take Photo</span>
                        </button>
                        <button
                            className={`method-button ${captureMethod === 'upload' ? 'active' : ''}`}
                            onClick={() => this.handleMethodChange('upload')}
                        >
                            <FaUpload className="method-icon" />
                            <span>Upload Photo</span>
                        </button>
                    </div>
                )}

                {/* Camera Capture */}
                {captureMethod === 'camera' && !imagePreview && (
                    <div className="camera-section">
                        {!isCapturing ? (
                            <Button
                                color="primary"
                                onClick={this.startCamera}
                                className="start-camera-btn"
                            >
                                <FaCamera className="mr-2" />
                                Start Camera
                            </Button>
                        ) : (
                            <div className="camera-container">
                                <video
                                    ref={this.videoRef}
                                    autoPlay
                                    playsInline
                                    className="camera-video"
                                />
                                <div className="camera-controls">
                                    <Button
                                        color="primary"
                                        onClick={this.capturePhoto}
                                        className="capture-btn"
                                    >
                                        Capture Photo
                                    </Button>
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={this.stopCamera}
                                        className="cancel-btn"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
                    </div>
                )}

                {/* File Upload */}
                {captureMethod === 'upload' && !imagePreview && (
                    <div className="upload-section">
                        <input
                            type="file"
                            ref={this.fileInputRef}
                            accept="image/*"
                            onChange={this.handleFileUpload}
                            style={{ display: 'none' }}
                        />
                        <Button
                            color="primary"
                            onClick={() => this.fileInputRef.current.click()}
                            className="upload-btn"
                        >
                            <FaUpload className="mr-2" />
                            Choose Photo
                        </Button>
                        <p className="upload-hint">Supported formats: JPEG, PNG (max 5MB)</p>
                    </div>
                )}

                {/* Image Preview & Verification */}
                {imagePreview && (
                    <div className="preview-section">
                        <div className="image-preview-wrapper">
                            <img src={imagePreview} alt="Preview" className="image-preview" />
                        </div>

                        {/* Verification Status */}
                        {verificationStatus === 'verifying' && (
                            <div className="verification-status verifying">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Verifying...</span>
                                </div>
                                <p>Verifying your identity...</p>
                            </div>
                        )}

                        {verificationStatus === 'success' && (
                            <div className="verification-status success">
                                <FaCheckCircle className="status-icon" />
                                <p>Verification Successful!</p>
                                {confidenceScore && (
                                    <p className="confidence-score">
                                        Confidence: {confidenceScore.toFixed(1)}%
                                    </p>
                                )}
                            </div>
                        )}

                        {verificationStatus === 'failed' && (
                            <div className="verification-status failed">
                                <FaTimesCircle className="status-icon" />
                                <p>Verification Failed</p>
                                {confidenceScore && (
                                    <p className="confidence-score">
                                        Confidence: {confidenceScore.toFixed(1)}%
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        {verificationStatus !== 'success' && (
                            <div className="preview-actions">
                                {verificationStatus !== 'verifying' && (
                                    <>
                                        <Button
                                            color="primary"
                                            onClick={this.handleVerify}
                                            className="verify-btn"
                                        >
                                            Verify Identity
                                        </Button>
                                        <Button
                                            color="secondary"
                                            outline
                                            onClick={this.handleRetake}
                                            className="retake-btn"
                                        >
                                            Retake Photo
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Error Messages */}
                {errorMessage && (
                    <div className="error-message">
                        <FaTimesCircle className="error-icon" />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {/* API Error Message (non-blocking) */}
                {apiError && (
                    <div className="error-message api-error">
                        <FaTimesCircle className="error-icon" />
                        <span>{apiError}</span>
                    </div>
                )}

                {/* Guidelines */}
                <div className="verification-guidelines">
                    <p className="guidelines-title">Photo Guidelines:</p>
                    <ul className="guidelines-list">
                        <li>Face should be clearly visible</li>
                        <li>Good lighting conditions</li>
                        <li>Remove sunglasses or face coverings</li>
                        <li>Look directly at the camera</li>
                    </ul>
                </div>
            </div>
        );
    }
}

export default FaceVerification;
